import type { AndroidBuild } from '@almox/shared';
import { api } from '../api/client.js';
import { useAsync } from '../components/ui.js';

/**
 * Tela "App": baixar o Almox para Android. O APK e gerado por
 * `apps/mobile/build-apk.sh` e servido pela propria API — nao passa por loja,
 * entao a instalacao pede a autorizacao de "fontes desconhecidas".
 */
export function AppPage() {
  const build = useAsync(() => api.androidBuild(), []);
  const info: AndroidBuild | null = build.data ?? null;

  return (
    <main className="page">
      <div className="page-head">
        <h1>App para Android</h1>
      </div>

      <section className="card">
        <div className="app-hero">
          <img src="/icone.svg" alt="" className="app-icon" />
          <div>
            <h2 style={{ marginTop: 0 }}>Almox no celular</h2>
            <p className="small muted" style={{ margin: 0 }}>
              A camera do celular tem autofoco — le codigo de barras muito melhor que a webcam do
              notebook. Instalado, o Almox ganha icone na tela inicial e abre em tela cheia, sem a
              barra do navegador.
            </p>
          </div>
        </div>

        {build.loading ? <p className="muted">Consultando a versao publicada…</p> : null}

        {info?.available ? (
          <>
            <a className="btn btn-primary btn-block btn-download" href="/api/v1/app/android/download">
              ⬇ Baixar o APK{info.versionName ? ` ${info.versionName}` : ''}
              {info.sizeBytes ? ` · ${Math.round(info.sizeBytes / 1024)} KB` : ''}
            </a>
            <table className="table app-meta">
              <tbody>
                <tr>
                  <th>Versao</th>
                  <td>
                    {info.versionName} (build {info.versionCode})
                  </td>
                </tr>
                <tr>
                  <th>Android minimo</th>
                  <td>{info.minAndroid} ou mais novo</td>
                </tr>
                <tr>
                  <th>Publicado em</th>
                  <td>{formatDateTime(info.builtAt)}</td>
                </tr>
                <tr>
                  <th>SHA-256</th>
                  <td>
                    <code className="hash">{info.sha256}</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        ) : null}

        {!build.loading && !info?.available ? (
          <p className="small" style={{ color: 'var(--danger)' }}>
            Nenhum APK publicado ainda. Rode <code>bash apps/mobile/build-apk.sh</code> no servidor e
            reconstrua o container.
          </p>
        ) : null}
      </section>

      <section className="card">
        <h2>Como instalar</h2>
        <ol className="steps">
          <li>
            Abra <strong>almox.tarefasjus.com/app</strong> no navegador do proprio celular e toque em
            baixar. (Baixar no computador nao adianta — o arquivo precisa estar no telefone.)
          </li>
          <li>
            O Android vai avisar que o arquivo pode ser prejudicial, porque nao veio da Play Store.
            Confirme o download.
          </li>
          <li>
            Toque no arquivo baixado. Na primeira vez o sistema pede para autorizar{' '}
            <strong>&ldquo;instalar apps desconhecidos&rdquo;</strong> para o navegador — autorize e
            volte.
          </li>
          <li>Toque em Instalar. O icone do Almox aparece na tela inicial.</li>
          <li>
            Ao abrir o scanner pela primeira vez, permita o acesso a <strong>camera</strong>.
          </li>
        </ol>
        <p className="small muted">
          O aviso de seguranca e normal para app fora da loja: ele diz que o Android nao conhece
          quem assinou o arquivo. A assinatura e a chave gerada neste servidor — da para conferir o
          SHA-256 acima com o do arquivo baixado.
        </p>
      </section>

      <section className="card">
        <h2>O que ele faz (e o que ainda nao faz)</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          Esta primeira versao e uma casca nativa em volta do site: entrega icone, tela cheia,
          camera com autofoco, o seletor de imagens da leitura por foto e o botao voltar navegando
          dentro do app.
        </p>
        <p className="small muted">
          <strong>Ainda depende de internet.</strong> Camera nativa, modo offline e sincronizacao
          sao o resto da fase 3 — sem rede, o app avisa em vez de mostrar tela em branco.
        </p>
      </section>
    </main>
  );
}

/** ISO no dado, dd/mm/aaaa hh:mm na tela (secao 6 do CLAUDE.md). */
function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '—';
  const dois = (valor: number) => String(valor).padStart(2, '0');
  return `${dois(data.getDate())}/${dois(data.getMonth() + 1)}/${data.getFullYear()} ${dois(data.getHours())}:${dois(data.getMinutes())}`;
}
